/**
 * TrialConversionMetrics
 *
 * Trial & Conversion Metrics section of the analytics dashboard.
 * Displays trial status, conversion performance, and basic to premium conversions.
 */
import React from 'react';
import { 
  UserCheck, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Target,
  DollarSign
} from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const TrialConversionMetrics = ({ analyticsData, formatCurrency, formatPercentage }) => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Trial & Conversion Metrics</h2>
      
      {/* Current Trial Status */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Current Trial Status</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Trial Users */}
          <AnalyticsTooltip content="Trial Users - Users currently in their trial period (subscriptionStatus = 'trialing'). These users have access to premium features but haven't started paying yet." id="trial-users-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                  <UserCheck className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Trial Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.totalTrialUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">currently in trial</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Non-Renewing Trial Users */}
          <AnalyticsTooltip content="Non-Renewing Trial Users - Trial users who have cancelled their trial and won't convert to paid subscriptions." id="non-renewing-trial-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg mr-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Non-Renewing Trial Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.totalNonRenewingTrialUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-orange-600 dark:text-orange-400">won't convert</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Potential Conversions */}
          <AnalyticsTooltip content="Potential Conversions - Trial users who haven't cancelled yet and could still convert to paid subscriptions." id="potential-conversions-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Potential Conversions</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {((analyticsData.summary?.totalTrialUsers || 0) - (analyticsData.summary?.totalNonRenewingTrialUsers || 0)).toLocaleString()}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">could still convert</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Potential Revenue */}
          <AnalyticsTooltip content="Potential Revenue - Estimated revenue if all current trial users convert to paid subscriptions. Calculated as trial users × current monthly plan price ($34)." id="potential-revenue-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Potential Revenue</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(((analyticsData.summary?.totalTrialUsers || 0) - (analyticsData.summary?.totalNonRenewingTrialUsers || 0)) * 34)}
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">if all convert</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>

      {/* Conversion Performance */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Conversion Performance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Trial Conversion Rate */}
          <AnalyticsTooltip content="Trial Conversion Rate - Percentage of users who started a trial and later converted to a paid subscription. This includes both active customers and customers who later deleted their accounts, providing a complete view of trial effectiveness." id="trial-conversion-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg mr-3">
                  <Target className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Trial Conversion</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatPercentage(analyticsData.summary?.trialToPayConversionRate)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">of trial users converted</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Trial Conversion Pre-Price Increase */}
          <AnalyticsTooltip content="Trial Conversion Pre-Price Increase - Conversion rate for users who signed up with $22/month pricing. Shows trial effectiveness during the lower pricing era." id="trial-conversion-pre-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg mr-3">
                  <TrendingDown className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Trial Conv. Pre-Increase</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatPercentage(analyticsData.summary?.prePriceIncreaseConversionRate)}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    {analyticsData.summary?.prePriceIncreaseConversions || 0}/{analyticsData.summary?.prePriceIncreaseTrialUsers || 0} converted
                  </p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Trial Conversion Post-Price Increase */}
          <AnalyticsTooltip content="Trial Conversion Post-Price Increase - Conversion rate for users who signed up with $34/month pricing. Shows trial effectiveness during the higher pricing era." id="trial-conversion-post-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Trial Conv. Post-Increase</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatPercentage(analyticsData.summary?.postPriceIncreaseConversionRate)}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {analyticsData.summary?.postPriceIncreaseConversions || 0}/{analyticsData.summary?.postPriceIncreaseTrialUsers || 0} converted
                  </p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Lifetime Trial Users */}
          <AnalyticsTooltip content="Total users who have ever started a trial (lifetime), including both active users and those who deleted their accounts. Helps you understand complete top-of-funnel interest over time." id="lifetime-trial-users-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Lifetime Trial Users</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.totalTrialUsersIncludingDeleted || analyticsData.summary?.lifetimeTrialUsers || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">ever started a trial</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>

      {/* Basic to Premium Conversions */}
      <div className="mb-6">
        <h3 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Basic to Premium Conversions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Basic to Premium Conversions */}
          <AnalyticsTooltip content="Basic to Premium Conversions - Users who created a basic account and later (more than 1 day later) started a premium trial or subscription. This shows how many users came back to upgrade after initially signing up as basic users." id="basic-to-premium-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/20 rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Basic to Premium Conversions</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {(analyticsData.summary?.basicToPremiumConversions || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-teal-600 dark:text-teal-400">users came back to upgrade</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Basic to Premium Rate */}
          <AnalyticsTooltip content="Basic to Premium Rate - Percentage of all users who started as basic and later upgraded to premium. Calculated as Basic-to-Premium Conversions ÷ Total Users. Shows how effective your product is at converting basic users over time." id="basic-to-premium-rate-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg mr-3">
                  <Target className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Basic to Premium Rate</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatPercentage(analyticsData.summary?.totalUsers > 0 ? 
                      (analyticsData.summary?.basicToPremiumConversions || 0) / analyticsData.summary.totalUsers * 100 : 0)}
                  </p>
                  <p className="text-xs text-cyan-600 dark:text-cyan-400">of all users upgraded</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>

          {/* Potential Revenue from Basic Users */}
          <AnalyticsTooltip content="Potential Revenue from Basic Users - Estimated additional revenue if all basic-to-premium conversions had happened immediately. Calculated as Basic-to-Premium Conversions × current monthly plan price ($34). Shows the value of nurturing basic users." id="potential-basic-revenue-tooltip">
            <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
              <div className="flex items-center">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg mr-3">
                  <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Value of Basic Conversions</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency((analyticsData.summary?.basicToPremiumConversions || 0) * 34)}
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">revenue from upgrades</p>
                </div>
              </div>
            </div>
          </AnalyticsTooltip>
        </div>
      </div>
    </div>
  );
};

export default TrialConversionMetrics;


